import clsx from "clsx";
import { Bot, CalendarClock, LayoutGrid, LogOut, Menu, MessageSquarePlus, Settings, Shield } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import TemplateMenu from "@/components/TemplateMenu";
import { Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe, useSetMe } from "@/lib/auth";
import { APP_NAME } from "@/lib/brand";
import { useServerEvents } from "@/lib/events";
import { useTabStatus } from "@/lib/tabStatus";
import { useUnreadCount } from "@/lib/tasks";
import type { User } from "@/lib/types";
import { voice } from "@/lib/voice/controller";

export function NavItem({ to, icon, children, end, badge }: { to: string; icon: ReactNode; children: ReactNode; end?: boolean; badge?: number }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        clsx(
          "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors",
          isActive ? "bg-zinc-200/70 font-medium dark:bg-zinc-800" : "hover:bg-zinc-200/50 dark:hover:bg-zinc-800/60",
        )
      }
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {!!badge && <span className="rounded-full bg-indigo-600 px-1.5 text-xs font-medium text-white tabular-nums">{badge}</span>}
    </NavLink>
  );
}

/**
 * Keeps the user's time zone in their settings, so the server tells the model the right local time
 * and schedules tasks in it (the server itself may run in UTC).
 */
function useReportTimezone(user: User | null, setMe: (u: User | null) => void) {
  useEffect(() => {
    if (!user) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz || user.settings.timezone === tz) return;
    api
      .patch<User>("/api/auth/me", { settings: { timezone: tz } })
      .then(setMe)
      .catch(() => undefined);
  }, [user, setMe]);
}

/** App shell: sidebar with navigation and a slot (`sidebar`) for page-specific content such as the chat list. */
/** Where to go once logged in (a page outside the normal flow asks for it). */
export const AFTER_LOGIN = "after-login";

export default function Layout({ sidebar }: { sidebar?: ReactNode }) {
  const me = useMe();
  const setMe = useSetMe();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useServerEvents(!!me.data);
  useTabStatus();
  useReportTimezone(me.data ?? null, setMe);
  const unread = useUnreadCount();
  voice.voice = (me.data?.settings.voice as string) || undefined;

  if (me.isLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  if (!me.data) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  const afterLogin = sessionStorage.getItem(AFTER_LOGIN);
  if (afterLogin) {
    sessionStorage.removeItem(AFTER_LOGIN);
    if (afterLogin.startsWith("/connect/companion?")) return <Navigate to={afterLogin} replace />;
  }
  const user = me.data;

  async function logout() {
    await api.post("/api/auth/logout").catch(() => undefined);
    setMe(null);
  }

  return (
    <div className="flex h-full">
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-zinc-200 bg-zinc-50 transition-transform md:static md:translate-x-0 dark:border-zinc-800 dark:bg-zinc-900",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        onClick={(e) => (e.target as HTMLElement).closest("a") && setOpen(false)}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <img src="/favicon.svg" alt="" className="size-6" />
            {APP_NAME}
          </Link>
        </div>
        <nav className="space-y-0.5 px-2">
          <NavItem to="/" end icon={<MessageSquarePlus size={18} />}>
            New chat
          </NavItem>
          <TemplateMenu />
          <NavItem to="/spaces" icon={<LayoutGrid size={18} />}>
            Spaces
          </NavItem>
          <NavItem to="/tasks" icon={<CalendarClock size={18} />} badge={unread.data}>
            Tasks
          </NavItem>
          <NavItem to="/agents" icon={<Bot size={18} />}>
            Agents
          </NavItem>
        </nav>
        <div className="mt-2 min-h-0 flex-1 overflow-y-auto px-2">{sidebar}</div>
        <div className="space-y-0.5 border-t border-zinc-200 p-2 dark:border-zinc-800">
          {user.role === "admin" && (
            <NavItem to="/admin" icon={<Shield size={18} />}>
              Admin
            </NavItem>
          )}
          <NavItem to="/settings" icon={<Settings size={18} />}>
            Settings
          </NavItem>
          <button
            onClick={logout}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-zinc-200/50 dark:hover:bg-zinc-800/60"
          >
            <LogOut size={18} />
            <span className="truncate">Sign out ({user.username})</span>
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setOpen(false)} />}
      <main className="flex min-w-0 flex-1 flex-col">
        <button
          className="absolute left-3 top-3 z-20 rounded-lg p-1.5 hover:bg-zinc-100 md:hidden dark:hover:bg-zinc-800"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <Outlet />
      </main>
    </div>
  );
}
